import type { EditTarget } from '@web-studio/shared-schemas';
import type { LayoutBlock, SpacingEnumKey } from '@web-studio/shared-schemas';
import type { MatrixLocale } from '@web-studio/core-fixtures';
import { useCallback, useRef, useState } from 'react';
import { FixtureInspector } from '@/components/FixtureInspector';
import { MatrixCanvas } from '@/components/MatrixCanvas';
import { ParityPanel } from '@/components/ParityPanel';

interface MatrixWorkbenchProps {
  blocks: LayoutBlock[];
  locale: MatrixLocale;
  themeId: string;
  colorsetId: string;
  spacingEnum: SpacingEnumKey;
  viewport: 'desktop' | 'tablet' | 'mobile';
  engineerMode: boolean;
  showEditTargets: boolean;
  showParity: boolean;
  onContentChange: (blockId: string, fieldKey: string, locale: string, value: string) => void;
  onMediaChange: (blockId: string, fieldKey: string, value: string) => void;
  onShowParityChange?: (value: boolean) => void;
  /** When set, only this block is editable in page mode. */
  activeBlockId?: string | null;
  /** Fill remaining viewport; canvas remaps hero dvh to the frame (no page scroll). */
  fitViewport?: boolean;
}

export function MatrixWorkbench({
  blocks,
  locale,
  themeId,
  colorsetId,
  spacingEnum,
  viewport,
  engineerMode,
  showEditTargets,
  showParity,
  onContentChange,
  onMediaChange,
  onShowParityChange,
  activeBlockId = null,
  fitViewport = false,
}: MatrixWorkbenchProps) {
  const canvasFrameRef = useRef<HTMLDivElement>(null);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [activeEditTarget, setActiveEditTarget] = useState<EditTarget | null>(null);

  const editable = engineerMode || showEditTargets;
  const inspectBlockId = activeBlockId ?? selectedBlockId ?? blocks[0]?.blockId ?? null;
  const inspectBlock = blocks.find((b) => b.blockId === inspectBlockId);
  const targetsActive = showEditTargets;
  const parityActive = engineerMode && showParity;

  const scrollBlockIntoView = useCallback((blockId: string) => {
    const root = canvasFrameRef.current;
    if (!root) return;
    const el = root.querySelector(`[data-block-id="${CSS.escape(blockId)}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  }, []);

  const handleBlockSelect = useCallback((blockId: string, editTarget?: EditTarget) => {
    setSelectedBlockId(blockId);
    setActiveEditTarget(editTarget ?? null);
  }, []);

  const handleInspectSelect = useCallback(
    (blockId: string) => {
      setSelectedBlockId(blockId);
      setActiveEditTarget(null);
      requestAnimationFrame(() => {
        scrollBlockIntoView(blockId);
      });
    },
    [scrollBlockIntoView],
  );

  return (
    <div
      className={
        fitViewport
          ? 'lab-workbench-stack lab-workbench-stack--fit'
          : 'lab-workbench-stack'
      }
    >
      <div
        className={[
          'lab-workbench',
          engineerMode ? '' : 'lab-workbench--preview',
          fitViewport ? 'lab-workbench--fit' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="lab-workbench__canvas">
          {targetsActive || engineerMode ? (
            <div className="lab-workbench__toggles">
              <span className="text-xs" style={{ color: 'var(--lab-muted)' }}>
                Click blocks and fields to select edit targets
              </span>
              <span className="text-xs" style={{ color: 'var(--lab-muted)' }}>
                Editing locale: <strong>{locale}</strong>
                {engineerMode ? ' · Engineer mode' : ' · Design mode'}
              </span>
            </div>
          ) : null}
          <div className={fitViewport ? 'lab-workbench__canvas-frame' : undefined}>
            <MatrixCanvas
              ref={canvasFrameRef}
              blocks={blocks}
              locale={locale}
              themeId={themeId}
              colorsetId={colorsetId}
              spacingEnum={spacingEnum}
              viewport={viewport}
              compact={fitViewport}
              editable={editable}
              showEditTargets={targetsActive}
              keepBlockBand={engineerMode}
              activeBlockId={editable ? inspectBlockId : null}
              activeEditTarget={editable ? activeEditTarget : null}
              onBlockSelect={editable ? handleBlockSelect : undefined}
            />
          </div>
        </div>

        {engineerMode ? (
          <div className="lab-workbench__sidebar">
            <FixtureInspector
              block={inspectBlock}
              blocks={blocks}
              selectedBlockId={inspectBlockId}
              onSelectBlock={blocks.length > 1 ? handleInspectSelect : undefined}
              locale={locale}
              engineerMode={engineerMode}
              activeEditTarget={activeEditTarget}
              onContentChange={(fieldKey, loc, value) => {
                if (inspectBlockId) onContentChange(inspectBlockId, fieldKey, loc, value);
              }}
              onMediaChange={(fieldKey, value) => {
                if (inspectBlockId) onMediaChange(inspectBlockId, fieldKey, value);
              }}
            />
          </div>
        ) : null}
      </div>

      {parityActive ? (
        <ParityPanel
          blocks={blocks}
          locale={locale}
          themeId={themeId}
          colorsetId={colorsetId}
          onClose={onShowParityChange ? () => onShowParityChange(false) : undefined}
        />
      ) : null}
    </div>
  );
}
